import { createContext, Dispatch, ReactElement, SetStateAction, useCallback, useMemo, useState } from "react";
import { FilterBody, FilterProps } from "../../../../../hooks/filter/typing";
import { InputModel } from "../../../../../utils/types";
import { Item } from "../../../../../utils/types/generals";
import { formatFilter } from "../pages/main/constans/constants"
import { useFilter } from "../../../../../hooks/filter";
import { useGetData } from "../pages/main/hooks/getData";
import { Categories, Priority, Subcategories, User, SubcategoriesAndParent } from "../../../../../service/catalog/response";
import { Priority as PriorityEnum } from "../../../../../utils/enums/priority";


interface ServiceContextType {
    services: Categories[];
    newsCategories: Categories[];
    newSubcategories: SubcategoriesAndParent[];
    editCategories: Categories[];
    editSubategories: Subcategories[];
    categoriesToDelete: number[];
    subcategoriesToDelete: number[];
    addService: (newService: Categories | Subcategories, parentId?: number) => void;
    addNewsService: (newService: Categories | SubcategoriesAndParent, parentId?: number) => void;
    editService: (id: number, name?: string, priority?: Priority, user?: User, requestText?: boolean) => void;
    editOldService: (id: number, name: string, priority?: Priority, user?: User, parentId?:number, requestText?: boolean) => void;
    removeService: (id: number) => void;
    removeNewsService: (id: number, parentId?: number) => void;
    initializeServices: (initialServices: Categories[]) => void;
    clearServices: () => void;
    clearNewsServices: () => void;
    editSubService: (parentId:number, arrayChildren:number[], proveedor: User | undefined) => void;
    //Filters
    filterData: FilterBody
    setFilterPayload: Dispatch<SetStateAction<FilterProps>>
    filterCatalog: FilterBody
    CatalogData: {
        items: {
            [x: string]: Item[];
        };
        input: {
            [x: string]: InputModel;
        };
        getData: () => Promise<void>;
    }
  }

const ServiceContext = createContext<ServiceContextType>({} as ServiceContextType);


const ServiceProvider = ({ children }: { children: ReactElement }) => {
  const [services, setServices] = useState<Categories[]>([])
  const [newsCategories, setNewsCategories] = useState<Categories[]>([])
  const [newSubcategories, setNewSubcategories] = useState<SubcategoriesAndParent[]>([])
  const [editCategories, setEditCategories] = useState<Categories[]>([])
  const [editSubategories, setEditSubategories] = useState<Subcategories[]>([])
  const [categoriesToDelete, setCategoriesToDelete] = useState<number[]>([])
  const [subcategoriesToDelete, setSubcategoriesToDelete] = useState<number[]>([])


  const addService = useCallback((newService: Categories | Subcategories, parentId?: number) => {
    const addServiceToParent = (services: Categories[], newService: Subcategories, parentId: number): Categories[] => {
      return services.map(service => {
        if (service.id === parentId) {
          return {
            ...service,
            subcategories: [...(service.subcategories || []), newService]
            };
        }
        return service;
    });
    };

    if (!parentId) {
      setServices((prevServices) => [...prevServices, newService as Categories]);
    } else {
      setServices((prevServices) => addServiceToParent(prevServices, newService as Subcategories, parentId ?? 0));
    }

  }, []);

  const editService = useCallback((id: number, name?: string, priority?: Priority, user?: User, requestText?: boolean) => {
    const editRecursively = (services: Categories[], id: number, updatedService: Partial<Categories | Subcategories>): Categories[] => {
      return services.map(service => {
        if (service.id === id) {
          return {
            ...service,
            ...updatedService
          };
        }

        if ('subcategories' in service) {
          const updatedSubcategories = service.subcategories.map(sub => {
            // Si el ID coincide con una subcategoría, actualízala
            if (sub.id === id) {
              return {
                ...sub,
                ...updatedService
              };
            }
            return sub;
          });

        // Devuelve la categoría con las subcategorías actualizadas
          return {
            ...service,
            subcategories: updatedSubcategories
          };
        }

        return service;
      });
    };

    setServices(prevServices => {
      return editRecursively(prevServices, id, { name, priority, user, requestText }) as Categories[];
    });
    
  }, []);

  const removeService = useCallback((id: number) => {
    const removeRecursively = (services: Categories[], id: number): Categories[] => {
      return services
        .filter(service => service.id !== id)
        .map(service => ({
          ...service,
          subcategories: service.subcategories 
            ? service.subcategories.filter(sub => sub.id !== id) : []
        }));
    }
  
    setServices(prevServices => removeRecursively(prevServices, id));
  }, []);

  const editSubService = useCallback((parentId: number, arrayIdChildren: number[], user: User | undefined) => {
      const editChildrenProveedores = (services: Categories[], parentId: number, arrayIdChildren: number[], user: User | undefined): Categories[] => {
        return services.map(service => {
          if (service.id === parentId) {
            return {
              ...service,
              subcategories: service.subcategories?.map(child => {
                if (arrayIdChildren.includes(child.id)) {
                  return {
                    ...child,
                    user: user
                  };
                }
                return child;
              })
            };
          }
  
          return service; 
        });
      };

      setServices(prevServices =>editChildrenProveedores(prevServices, parentId, arrayIdChildren, user));
    },
    []
  );

  const initializeServices = useCallback((initialServices: Categories[]) => {
    setServices(initialServices);
  }, []);

  const clearServices = useCallback(() => {
    setServices([]);
  }, []);

  const clearNewsServices = useCallback(() => {
    setNewsCategories([])
    setNewSubcategories([])
    setCategoriesToDelete([])
    setSubcategoriesToDelete([])
    setEditSubategories([])
    setEditCategories([])
  }, []);

  const addNewsService = useCallback((newService: Categories | SubcategoriesAndParent, parentId?: number) => {
    const addServiceToParent = (services: Categories[], newService: Subcategories, parentId: number): Categories[] => {
      return services.map(service => {
        if (service.id === parentId) {
          return {
            ...service,
            subcategories: [...(service.subcategories || []), newService]
            };
        }
        return service;
    });
    };

    if (!parentId) {
      setNewsCategories((prevServices) => [...prevServices, newService as Categories]);
    } else {
      if( newsCategories.some(category => category.id === parentId) ){
        setNewsCategories((prevServices) => addServiceToParent(prevServices, newService as Subcategories, parentId ?? 0));
      }else{
        setNewSubcategories((prevServices) => [...prevServices, newService as SubcategoriesAndParent]);
      }
    }

  }, [newSubcategories,newsCategories]);


  const removeNewsService = useCallback((id: number, parentId?:number) => {

    if (!parentId) {

      if (newsCategories.some(category => category.id === id)) {
        setNewsCategories(prevServices => prevServices.filter(service => service.id !== id))
      }else{
        setCategoriesToDelete((prevServices) => [...prevServices, id]);
      }

    } else {

      if (newsCategories.some(category => category.id === parentId)) {
        setNewsCategories(prevServices => prevServices.map(service => ({
          ...service,
          subcategories: service.subcategories 
            ? service.subcategories.filter(sub => sub.id !== id) : []
        })) )
      }else if(newSubcategories.some(subcategory => subcategory.id === id)){
        setNewSubcategories(prevServices => prevServices.filter(service => service.id !== id))
      } else {
        setSubcategoriesToDelete((prevServices) => [...prevServices, id]);
      }
      
    }

  }, [newsCategories,newSubcategories,categoriesToDelete,subcategoriesToDelete]);

  const editOldService = useCallback((id: number, name: string, priority?: Priority, user?: User, parentId?: number, requestText?: boolean)=>{

    if(!parentId){

      if(newsCategories.some((category) => category.id === id)){

        setNewsCategories((prevCategories)=>prevCategories.map((category) =>
          category.id === id ? { ...category, name } : category
        ))

      }else{

        setEditCategories((prevCategories) => {
          const exists = prevCategories.some((category) => category.id === id);
  
          if (exists) {
            return prevCategories.map((category) =>
              category.id === id ? { ...category, name } : category
            );
          } else {
            return [...prevCategories, { id, name, subcategories: [] }];
          }
  
        });

      }

    }else{

      const updatedService: Partial<Categories | Subcategories> = ({id,name, priority, user, requestText})
      const defaultPriority: Priority = {id: PriorityEnum.HIGH ,name:''}

      if(newsCategories.some((category) => category.id === parentId)){

        setNewsCategories((prevCategories) =>
          prevCategories.map((category) =>
            category.id === parentId
              ? {
                  ...category,
                  subcategories: category.subcategories.map((subcategory) =>
                    subcategory.id === id
                      ? { ...subcategory, ...updatedService } 
                      : subcategory
                  ),
                }
              : category
          )
        );

      } else {

        if(newSubcategories.some((category) => category.id === id)){

          setNewSubcategories((prevSubCategories)=>prevSubCategories.map((subcategory) =>
            subcategory.id === id ? { ...subcategory, ...updatedService } : subcategory
          ))
  
        }else{
  
          setEditSubategories((prevSubCategories) => {
            const exists = prevSubCategories.some((subcategory) => subcategory.id === id);

            if (exists) {
              return prevSubCategories.map((subcategory) =>
                subcategory.id === id ? { ...subcategory, ...updatedService } : subcategory
              );
            } else {
              return [...prevSubCategories, { id, name,  priority: priority || defaultPriority,  user: user || null, requestText: !!requestText} ];
            }
            
          });
  
        }

      }
      
    }
  }, [editCategories, editSubategories,newSubcategories,newsCategories])


  //Filters

  const [filterPayload, setFilterPayload] = useState<FilterProps>({ formatFilter: {} })
  const filterData = useFilter(filterPayload)
  const CatalogData = useGetData()
  const filterCatalog = useFilter({ formatFilter })

  const value = useMemo(() => ({ services, addService, addNewsService, removeService, removeNewsService, initializeServices, clearServices, clearNewsServices, editService, editOldService, editSubService, setFilterPayload, filterData, CatalogData, filterCatalog, newsCategories, newSubcategories, categoriesToDelete, subcategoriesToDelete, editCategories, editSubategories }), [
    services,
    addService,
    addNewsService,
    removeService,
    removeNewsService,
    initializeServices,
    clearServices,
    clearNewsServices,
    editService,
    editOldService,
    editSubService,
    setFilterPayload, 
    filterData, 
    CatalogData, 
    filterCatalog,
    newsCategories, 
    newSubcategories, 
    categoriesToDelete, 
    subcategoriesToDelete,
    editCategories, 
    editSubategories
  ]);

  return (
    <ServiceContext.Provider value={value}>
      {children}
    </ServiceContext.Provider>
  );
};

export { ServiceContext, ServiceProvider };
